import {
  ControlType,
  DateTimeType,
  getCountriesList,
  allowedConditionTypes,
  setTableCtrlValueToNull,
  adjustConditionsForDateMode,
} from './form-lib';
import { isString, compareTimes, parseDateStringForFormat, parseDateStringWithOrdinalIndicator } from './utils';

function isDateConditionSatisfied(ctrl, condType) {
  const mode =
    ctrl.props.type === DateTimeType.DATE || ctrl.props.type === DateTimeType.DATETIME ? ctrl.props.mode : 'single';
  const disableYearEffect = ctrl.props.type === DateTimeType.DATE && ctrl.props?.disable_year;
  const allowedConditionType = allowedConditionTypes()[ctrl.type].filter(
    (cond) => !adjustConditionsForDateMode(mode, disableYearEffect).includes(cond)
  );
  return allowedConditionType.includes(condType);
}

const strContain = (ctrlValue, condParam) => {
  if (!ctrlValue || !condParam) {
    return false;
  }
  return ctrlValue.toLocaleLowerCase().indexOf(condParam.toLocaleLowerCase()) > -1;
};

const dateContain = (ctrl, condParam, condType) => {
  const {
    value: ctrlValue,
    props: { mode },
  } = ctrl;

  if (!ctrlValue || !condParam || mode !== 'multiple' || !isDateConditionSatisfied(ctrl, condType)) {
    return false;
  }

  const params = condParam.split(', ').map((item) => item.trim());
  const values = ctrlValue.split(', ').map((item) => item.trim());

  const valueSet = new Set(values);

  // Check if any item in the 'params' array is included in the 'valueSet'
  return params.some((item) => valueSet.has(item));
};

const dateLaterThan = (ctrl, condParam, condType, isLater) => {
  const {
    value: ctrlValue,
    props: { mode, type: dateType, date_format: dateFormat, time_format: timeFormat, separator, disable_year = false },
  } = ctrl;

  if (!ctrlValue || !condParam || mode !== 'single' || !isDateConditionSatisfied(ctrl, condType)) return false;

  const disableYearEffect = dateType === DateTimeType.DATE && disable_year;

  const parseTime = (time) =>
    timeFormat === '24' ? new Date(`1970-01-01T${time}:00`).getTime() : compareTimes(time, condParam);

  const parseDate = (date) =>
    disableYearEffect
      ? parseDateStringWithOrdinalIndicator(date).getTime()
      : parseDateStringForFormat(date, dateFormat, separator).getTime();

  const compare = (value, target) => (isLater ? value > target : value < target);

  if (dateType === DateTimeType.DATE || dateType === DateTimeType.DATETIME) {
    return compare(parseDate(ctrlValue), parseDate(condParam));
  } else if (dateType === DateTimeType.TIME) {
    return compare(parseTime(ctrlValue), timeFormat === '24' ? parseTime(condParam) : 0);
  }
};

const dateLaterThanEqual = (ctrl, condParam, condType, isLater) => {
  const {
    value: ctrlValue,
    props: { mode, type: dateType, date_format: dateFormat, time_format: timeFormat, separator, disable_year = false },
  } = ctrl;

  if (!ctrlValue || !condParam || mode !== 'single' || !isDateConditionSatisfied(ctrl, condType)) return false;

  const disableYearEffect = dateType === DateTimeType.DATE && disable_year;

  const parseTime = (time) =>
    timeFormat === '24' ? new Date(`1970-01-01T${time}:00`).getTime() : compareTimes(time, condParam);

  const parseDate = (date) =>
    disableYearEffect
      ? parseDateStringWithOrdinalIndicator(date).getTime()
      : parseDateStringForFormat(date, dateFormat, separator).getTime();

  const compare = (value, target) => (isLater ? value >= target : value <= target);

  if (dateType === DateTimeType.DATE || dateType === DateTimeType.DATETIME) {
    return compare(parseDate(ctrlValue), parseDate(condParam));
  } else if (dateType === DateTimeType.TIME) {
    return compare(parseTime(ctrlValue), timeFormat === '24' ? parseTime(condParam) : 0);
  }
};

const isCheckboxEmpty = (checkboxValue) => {
  if (!checkboxValue) {
    return true;
  }
  for (const key in checkboxValue) {
    if (checkboxValue[key] !== undefined && checkboxValue.hasOwnProperty(key)) {
      return false;
    }
  }
  return true;
};

const checkboxEqual = (checkboxValue, value) => {
  if (!checkboxValue || typeof checkboxValue !== 'object') {
    return false;
  }
  return checkboxValue[value];
};

const checkboxContain = (checkboxValue, value) => {
  if (!checkboxValue || !value || typeof checkboxValue !== 'object') {
    return false;
  }
  return Object.keys(checkboxValue)
    .map(function (key) {
      const v = key !== ControlType.OptOther.STRING ? key : checkboxValue[key];
      return strContain(v, value);
    })
    .reduce(function (acc, cur) {
      return acc || cur;
    }, false);
};

const multiContain = (valueArr, value) => {
  if (!valueArr || !value || !Array.isArray(valueArr)) {
    return false;
  }

  return valueArr
    .map(function (v) {
      return strContain(v, value);
    })
    .reduce(function (acc, cur) {
      return acc || cur;
    }, false);
};

const isMultiselectEmpty = (multiselectValue) => !multiselectValue || !multiselectValue.length;

const multiselectEqual = (multiselectValue, value) => {
  if (!multiselectValue || !Array.isArray(multiselectValue)) {
    return false;
  }
  return multiselectValue.indexOf(value) > -1;
};

const fileEmpty = (file) => !file || !file.size;

const getMatrixColumnHeaders = (ctrl) => {
  const clickedColumnHeaders = [];
  if (ctrl.value && ctrl.value.length) {
    for (let v = 0; v < ctrl.value.length; v += 1) {
      const val = ctrl.value[v]?.split(':')?.pop()?.trim();
      if (val) {
        if (ctrl.props.column_headers.indexOf(val) >= 0) {
          clickedColumnHeaders.push(ctrl.props.column_headers.indexOf(val) + 1);
        } else {
          clickedColumnHeaders.push('');
        }
      }
    }
  }
  return clickedColumnHeaders;
};

const matrixEmpty = (ctrl) => {
  const valueArr = getMatrixColumnHeaders(ctrl);
  if (!valueArr || !valueArr.length) {
    return true;
  }
  return valueArr
    .map(function (v) {
      return v === '';
    })
    .reduce(function (acc, cur) {
      return acc && cur;
    }, true);
};

const matrixEqual = (ctrl, condParam) => {
  const matrixValue = getMatrixColumnHeaders(ctrl);
  const valueObj = isString(condParam) ? JSON.parse(condParam) : condParam;
  if (!matrixValue || !valueObj) {
    return false;
  }
  return matrixValue[valueObj.row] === valueObj.val + 1;
};

const greaterThan = (ctrlValue, condParam, isGreater) => {
  if (!ctrlValue || !condParam) return false;

  const ctrlValueNum = parseFloat(ctrlValue);
  const condParamNum = parseFloat(condParam);

  if (isNaN(ctrlValueNum) || isNaN(condParamNum)) return false;

  return isGreater ? ctrlValueNum > condParamNum : ctrlValueNum < condParamNum;
};

const greaterThanEqual = (ctrlValue, condParam, isGreater) => {
  if (!ctrlValue || !condParam) return false;

  const ctrlValueNum = parseFloat(ctrlValue);
  const condParamNum = parseFloat(condParam);

  if (isNaN(ctrlValueNum) || isNaN(condParamNum)) return false;

  return isGreater ? ctrlValueNum >= condParamNum : ctrlValueNum <= condParamNum;
};

const between = (ctrlValue, condParam) => {
  if (!ctrlValue || !condParam) return false;

  const ctrlValueNum = parseFloat(ctrlValue);
  const condParamNumArr = condParam.split(',').map(item => parseFloat(item.trim()));

  if (isNaN(ctrlValueNum) || condParamNumArr.some(item => isNaN(item))) return false;

  return ctrlValueNum >= Math.min(...condParamNumArr) && ctrlValueNum <= Math.max(...condParamNumArr);
};

const isEmpty = (value) => !value || value === 'null';

const isRuleCaseSatisfied = (condType, ctrl, condParam) => {
  let ctrlValue = ctrl?.value;
  if (ctrl.type === ControlType.CHECKBOXES) {
    switch (condType) {
      case 'empty':
        return isCheckboxEmpty(ctrlValue);
      case 'not_empty':
        return !isCheckboxEmpty(ctrlValue);
      case 'equal':
        return checkboxEqual(ctrlValue, condParam);
      case 'not_equal':
        return !checkboxEqual(ctrlValue, condParam);
      case 'contain':
        return checkboxContain(ctrlValue, condParam);
      case 'not_contain':
        return !checkboxContain(ctrlValue, condParam);
    }
  } else if (ctrl.type === ControlType.SELECT_MULTIPLE) {
    switch (condType) {
      case 'empty':
        return isMultiselectEmpty(ctrlValue);
      case 'not_empty':
        return !isMultiselectEmpty(ctrlValue);
      case 'equal':
        return multiselectEqual(ctrlValue, condParam);
      case 'not_equal':
        return !multiselectEqual(ctrlValue, condParam);
      case 'contain':
        return multiContain(ctrlValue, condParam);
      case 'not_contain':
        return !multiContain(ctrlValue, condParam);
    }
  } else if (ctrl.type === ControlType.MATRIX_RATING) {
    switch (condType) {
      case 'empty':
        return matrixEmpty(ctrl);
      case 'not_empty':
        return !matrixEmpty(ctrl);
      case 'equal':
        return matrixEqual(ctrl, condParam);
      case 'not_equal':
        return !matrixEqual(ctrl, condParam);
    }
  } else if (ctrl.type === ControlType.FILE_UPLOAD) {
    switch (condType) {
      case 'empty':
        return fileEmpty(ctrlValue);
      case 'not_empty':
        return !fileEmpty(ctrlValue);
    }
  } else if (ctrl.type === ControlType.SIGNATURE) {
    switch (condType) {
      case 'empty':
        return fileEmpty(ctrlValue);
      case 'not_empty':
        return !fileEmpty(ctrlValue);
    }
  } else if (ctrl.type === ControlType.DATETIME) {
    const disableYearEffect = ctrl.props.type === DateTimeType.DATE && ctrl.props?.disable_year;
    switch (condType) {
      case 'greater_than':
        return dateLaterThan(ctrl, condParam, condType, true);
      case 'greater_than_equal':
        return dateLaterThanEqual(ctrl, condParam, condType, true);
      case 'less_than':
        return dateLaterThan(ctrl, condParam, condType, false);
      case 'less_than_equal':
        return dateLaterThanEqual(ctrl, condParam, condType, false);
      case 'contain':
        return disableYearEffect ? strContain(ctrlValue, condParam) : dateContain(ctrl, condParam, condType);
      case 'not_contain':
        return disableYearEffect ? !strContain(ctrlValue, condParam) : !dateContain(ctrl, condParam, condType);
    }
  }

  if (isString(ctrlValue) && isString(condParam)) {
    ctrlValue = ctrlValue.toLocaleLowerCase().trim();
    condParam = condParam.toLocaleLowerCase().trim();
  }

  switch (condType) {
    case 'empty':
      return isEmpty(ctrlValue);
    case 'not_empty':
      return !isEmpty(ctrlValue);
    case 'equal':
      return ctrlValue === condParam;
    case 'not_equal':
      return ctrlValue !== condParam;
    case 'contain':
      return strContain(ctrlValue, condParam);
    case 'not_contain':
      return !strContain(ctrlValue, condParam);
    case 'greater_than':
      return greaterThan(ctrlValue, condParam, true);
    case 'greater_than_equal':
      return greaterThanEqual(ctrlValue, condParam, true);
    case 'less_than':
      return greaterThan(ctrlValue, condParam, false);
    case 'less_than_equal':
      return greaterThanEqual(ctrlValue, condParam, false);
    case 'between':
      return between(ctrlValue, condParam);
  }

  return false;
};

const isRuleSatisfied = function (rule, ctrl, ctrlIndex) {
  if (rule.option) {
    if (rule.option === 'all') {
      return rule.triggers.reduce((acc, trigger) => {
        const condType = trigger.type;
        const condParam = trigger.param;
        const tCtrl = ctrlIndex[trigger.id];
        return acc && isRuleCaseSatisfied(condType, tCtrl, condParam);
      }, true);
    }
    if (rule.option === 'any') {
      return rule.triggers.reduce(function (acc, trigger) {
        const condType = trigger.type;
        const condParam = trigger.param;
        const tCtrl = ctrlIndex[trigger.id];
        return acc || isRuleCaseSatisfied(condType, tCtrl, condParam);
      }, false);
    }
  } else if (rule.triggering_field_id) {
    // <legacy rule format
    const condType = rule.condition_type;
    const condParam = rule.condition_param;
    return isRuleCaseSatisfied(condType, ctrl, condParam);
  }
  return false;
};

const isAnyLegacyRuleSatisfied = function (triggeredFieldId, formRules, ctrlIndex) {
  for (let r = 0; r < formRules.length; r++) {
    const rule = formRules[r];
    if (rule.triggered_field_id === triggeredFieldId) {
      const ctrl = ctrlIndex[rule.triggering_field_id];
      if (isRuleSatisfied(rule, ctrl, ctrlIndex)) {
        return true;
      }
    }
  }
  return false;
};

const isAnyRuleSatisfied = function isAnyRuleSatisfied(action, actionFields, ctrl, ctrlIndex) {
  const actionRules = actionFields[action.id] || [];
  return actionRules.reduce(function (acc, rule) {
    return acc || isRuleSatisfied(rule, ctrl, ctrlIndex);
  }, false);
};

export const isTriggerField = (triggers, ctrlId) => {
  return triggers.hasOwnProperty(ctrlId);
};

const resetTableFieldValue = (ctrl) => setTableCtrlValueToNull(ctrl);

const resetMatrixFieldValue = (ctrl) => {
  return ctrl.props.row_headers.map((rowHeader) => `${rowHeader}: undefined`);
};

export const applyLogic = (iid, ctrlIndex, triggeringFields, actionFields, form, hiddenFields) => {
  const { pages, rules: formRules } = form.definition;
  const ctrl = ctrlIndex[iid];
  const ctrlRules = triggeringFields[iid];

  const resetHiddenControlValue = (control) => {
    const isPage = control.type === 'page';

    const resetValue = (ctrl) => {
      if (ctrl.type === ControlType.TABLE_FIELD) return resetTableFieldValue(ctrl);
      if (ctrl.type === ControlType.MATRIX_RATING) return resetMatrixFieldValue(ctrl);
      return null;
    };

    if (!isPage) {
      // Reset the corresponding value in ctrlIndex
      control.value = resetValue(control);

      // Reset the corresponding value in pages
      for (const page of pages) {
        const pageControl = page.controls.find((pageCtrl) => pageCtrl.internal_id === control.internal_id);
        if (pageControl) {
          pageControl.value = resetValue(pageControl);
        }
      }
    } else {
      // Reset all values in a page
      const page = pages.find((page) => page.internal_id === control.internal_id);
      if (page) {
        for (const pageControl of page.controls) {
          // Reset each page control value in pages
          pageControl.value = resetValue(pageControl);

          // Reset each page control value in ctrlIndex
          ctrlIndex[pageControl.internal_id].value = resetValue(pageControl);
        }
      }
    }
  };

  const processActionInRowOrPage = (shouldHide, control) => {
    const isPage = control.type === 'page';
    const isAddRow = control.type === 'add_row';

    const updateHiddenFields = (controlIDs) => {
      for (const id of controlIDs) {
        // If the control ID is not in actionFields or is in actionFields but not already hidden
        if (!actionFields[id] || !hiddenFields[id]) {
          hiddenFields[id] = shouldHide;
        }
      }
    };

    if (isAddRow) {
      updateHiddenFields(control.props.controls);
    }

    if (isPage) {
      const page = pages[control.index];
      const pageControlIds = page.controls.map((ctrl) => ctrl.internal_id);
      updateHiddenFields(pageControlIds);
    }
  };

  for (const rule of ctrlRules) {
    if (isRuleSatisfied(rule, ctrl, ctrlIndex)) {
      if (rule.option) {
        for (const action of rule.actions) {
          const { id: triggeredFieldId, action: actionType } = action;
          const triggeredCtrl = ctrlIndex[triggeredFieldId];

          if (triggeredCtrl) {
            const shouldHide = actionType === 'hide';
            hiddenFields[triggeredFieldId] = shouldHide;

            if (shouldHide) {
              resetHiddenControlValue(triggeredCtrl);
            }

            processActionInRowOrPage(shouldHide, triggeredCtrl);
          }
        }
      } else if (rule.triggering_field_id) {
        // as rules are ORed it's enough that one of them is satisfied
        // for given triggered_field_id
        const { triggered_field_id: triggeredFieldId, action: actionType } = rule;
        const triggeredCtrl = ctrlIndex[triggeredFieldId];

        if (triggeredCtrl) {
          const shouldHide = actionType === 'hide';
          hiddenFields[triggeredFieldId] = shouldHide;

          if (shouldHide) {
            resetHiddenControlValue(triggeredCtrl);
          }

          processActionInRowOrPage(shouldHide, triggeredCtrl);
        }
      }
    } else if (rule.option) {
      for (const action of rule.actions) {
        if (!isAnyRuleSatisfied(action, actionFields, ctrl, ctrlIndex)) {
          const { id: triggeredFieldId, action: actionType } = action;
          const triggeredCtrl = ctrlIndex[action.id];

          if (triggeredCtrl) {
            const shouldHide = actionType === 'show';
            hiddenFields[triggeredFieldId] = shouldHide;

            if (shouldHide) {
              resetHiddenControlValue(triggeredCtrl);
            }

            processActionInRowOrPage(shouldHide, triggeredCtrl);
          }
        }
      }
    } else if (rule.triggering_field_id) {
      // because rules are ORed we have to check if no other rule is
      // satisified for the same triggered_field_id
      if (!isAnyLegacyRuleSatisfied(rule.triggered_field_id, formRules, ctrlIndex)) {
        const { triggered_field_id: triggeredFieldId, action: actionType } = rule;
        const triggeredCtrl = ctrlIndex[triggeredFieldId];

        if (triggeredCtrl) {
          const shouldHide = actionType === 'show';
          hiddenFields[triggeredFieldId] = shouldHide;

          if (shouldHide) {
            resetHiddenControlValue(triggeredCtrl);
          }

          processActionInRowOrPage(shouldHide, triggeredCtrl);
        }
      }
    }
  }
};

export default {
  isTriggerField,
  applyLogic,
};
